import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, of, Subject, Subscription, take, takeUntil } from 'rxjs';
import { DateTimeFormattingService } from '@capital-access/common/formatting';
import { LocalizationService } from '@capital-access/common/localization';
import { LoadStatus } from '@capital-access/common/utils';
import { Alert, FireflyDrawerContent, FireflyDrawerService } from '@capital-access/firefly/components';
import {
  getReportType,
  PAGE_SIZES,
  PageConfig,
  ReportingTemplateRequest,
  ReportSectionConfigurationWithOrderImpl,
  ReportTemplateFormType,
  ReportType,
  serializeReportSections,
  TemplateLibraryService
} from '@capital-access/reporting/common';
import { ReportTypeReportSections } from '@capital-access/reporting/report-section';
import {
  createTemplateFailure,
  createTemplateSuccess,
  saveCreateReportForm,
  submitReportFormDuplicate
} from '../../+store/actions';
import {
  getButtonType,
  getCreateReportSectionSettings,
  getReportingTemplateRequest,
  getSaveDrawerLoadStatus,
  getSelectedTemplateForPreview,
  getTemplateDrawerAction,
  templatesLimitAlmostReached
} from '../../+store/selectors';
import { REPORTING_TEMPLATE_PAGE } from '../../constants';
import { TemplateLibraryRepository } from '../../services/template-library.repository';
import { getForm } from '../../utils/forms';
@Component({
  selector: 'ca-report-template-drawer-form',
  templateUrl: './report-template-drawer-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ReportTypeReportSections]
})
export class ReportTemplateDrawerFormComponent
  extends FireflyDrawerContent<{ skipConfirmationModal: boolean }>
  implements OnInit, OnDestroy
{
  readonly localizationScope = REPORTING_TEMPLATE_PAGE;
  form!: FormGroup;
  Alert = Alert;
  pageSizes: PageConfig[] = [];
  showFormValidationError = false;
  destroyed$ = new Subject<void>();
  buttonType$: Observable<string | null>;
  reportType!: ReportType;
  ReportType = ReportType;
  localizedReportType!: Observable<string>;
  drawerAction$: Observable<ReportTemplateFormType | null>;
  reportTemplateFormType = ReportTemplateFormType;
  subscriptions: Subscription[] = [];
  saveStatus: LoadStatus = LoadStatus.Loaded;
  loadStatus = LoadStatus;
  selectedTemplateDetails$!: Observable<{ userId: number; dateTime: Date | undefined } | null>;
  templatesLimitAlmostReached$!: Observable<boolean>;
  showBannerForTemplateLimit = true;
  showTemplateCreateError = false;
  createReportSectionSettings!: string | null;
  private dateTimeFormattingService = inject(DateTimeFormattingService);
  isTemplateLimitError = false;
  constructor(
    private drawerService: FireflyDrawerService,
    private store: Store,
    private reportSections: ReportTypeReportSections,
    private changeDetector: ChangeDetectorRef,
    private templateLibraryRepository: TemplateLibraryRepository,
    private templateLibrarySvc: TemplateLibraryService,
    private localizationService: LocalizationService
  ) {
    super();
    this.buttonType$ = this.store.select(getButtonType);
    this.drawerAction$ = this.store.select(getTemplateDrawerAction);
    this.saveStatus = LoadStatus.Loaded;
    this.initForm();
  }
  ngOnInit(): void {
    this.drawerService.setForm(this.form);
    this.subscriptions.push(
      this.store.select(getReportType).subscribe(reportType => {
        if (reportType) {
          this.reportType = reportType;
          this.localizedReportType = this.localizationService.localize(
            `reportType${this.reportType}`,
            {},
            this.localizationScope
          );
          // Update page sizes whenever report type changes
          this.updatePageSizes();
        }
      })
    );
    this.subscriptions.push(
      this.store.select(getSaveDrawerLoadStatus).subscribe(status => {
        // Always update saveStatus, even for Pristine state
        this.saveStatus = status === LoadStatus.Pristine ? LoadStatus.Loaded : status;
        if (status === LoadStatus.Loaded) {
          this.drawerService.setForm(this.form);
        }
        this.changeDetector.markForCheck();
      })
    );
    this.subscriptions.push(
      this.store
        .select(getCreateReportSectionSettings)
        .subscribe(settings => (this.createReportSectionSettings = settings))
    );
    this.selectedTemplateDetails$ = this.store.select(getSelectedTemplateForPreview).pipe(
      map(template => {
        if (!template || template?.caTemplateInd) {
          return null;
        }
        if (template) {
          return template?.updatedBy
            ? { userId: template?.updatedBy, dateTime: template?.updatedDttm }
            : {
                userId: template?.createdBy,
                dateTime: template?.createdDttm
              };
        }
        return null;
      })
    );
    this.templatesLimitAlmostReached$ = this.store.select(templatesLimitAlmostReached);
  }
  private updatePageSizes(): void {
    if (this.reportType === ReportType.Itinerary) {
      // For Itinerary, show only landscape options
      this.pageSizes = [
        {
          ...PAGE_SIZES['us-letter'],
          label: 'usLetterItinerary',
          value: 'us-letter',
          width: PAGE_SIZES['us-letter-itinerary'].width,
          height: PAGE_SIZES['us-letter-itinerary'].height
        },
        {
          ...PAGE_SIZES['a4'],
          label: 'a4sizeItinerary',
          value: 'a4',
          width: PAGE_SIZES['a4-itinerary'].width,
          height: PAGE_SIZES['a4-itinerary'].height
        }
      ];
    } else {
      // For other report types, show normal portrait options
      this.pageSizes = [
        {
          ...PAGE_SIZES['us-letter'],
          label: PAGE_SIZES['us-letter'].label
        },
        {
          ...PAGE_SIZES['a4'],
          label: PAGE_SIZES['a4'].label
        }
      ];
    }
    this.changeDetector.markForCheck();
  }
  initForm(): void {
    combineLatest([this.store.select(getReportingTemplateRequest), this.drawerAction$])
      .pipe(take(1))
      .subscribe(([formValues, actionType]): void => {
        this.form = getForm(actionType as ReportTemplateFormType, formValues as ReportingTemplateRequest, {
          templateLibraryRepository: this.templateLibraryRepository,
          changeDetector: this.changeDetector
        });
        this.form.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(status => {
          this.showFormValidationError = status === 'INVALID' && !this.templateNameCtrl?.pristine;
          this.changeDetector.detectChanges();
        });
      });
  }

  private getReportingTemplateRequest(): Observable<ReportingTemplateRequest> {
    let settings = this.createReportSectionSettings;
    const serializedData = serializeReportSections(this.reportSections.getReportSections(this.reportType));
    const sectionConfigs: SerializedReportSection[] = JSON.parse(serializedData);

    const mappedSectionConfigs = sectionConfigs.map((section: SerializedReportSection) => {
      section.config = JSON.parse(section.config as unknown as string);

      if (Array.isArray(section.config.nestedItems)) {
        section.config.nestedItems = section.config.nestedItems.map((nestedItem: NestedItem) => ({
          ...nestedItem,
          nestedItemSectionConfiguration: {
            ...nestedItem.nestedItemSectionConfiguration,
            config: JSON.parse(nestedItem.nestedItemSectionConfiguration.config)
          }
        }));
      }

      return section;
    });

    // Map page size for itinerary templates
    let selectedPageSize = this.form.value.pageSize;
    if (this.reportType === ReportType.Itinerary) {
      if (selectedPageSize === 'us-letter') {
        selectedPageSize = 'us-letter-itinerary';
      } else if (selectedPageSize === 'a4') {
        selectedPageSize = 'a4-itinerary';
      }
    }

    if (!settings) {
      settings = JSON.stringify({ pageSize: selectedPageSize, configurations: mappedSectionConfigs });
    } else {
      const parsedSetting = JSON.parse(settings);
      parsedSetting.pageSize = selectedPageSize;
      const existingSectionIds = new Set(
        parsedSetting.configurations.map((section: ReportSectionConfigurationWithOrderImpl) => section.config.name)
      );
      mappedSectionConfigs.forEach(section => {
        if (!existingSectionIds.has(section.config.name)) {
          section.isSelected = false;
          section.config.appliedReportType = this.reportType;
          section.order = parsedSetting.configurations.length;
          parsedSetting.configurations.push(section);
        }
      });
      settings = JSON.stringify(parsedSetting);
    }

    const formData = {
      ...this.form.value,
      reportType: this.reportType,
      isDefault: true,
      settings,
      pagesCount: 1
    } as ReportingTemplateRequest;
    delete formData.pageSize;
    return of(formData);
  }
  saveAndContinue(): void {
    if (this.form.valid) {
      this.getReportingTemplateRequest()
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe({
          next: request => {
            this.showBannerForTemplateLimit = true;
            this.templateLibrarySvc
              .creatingTemplateRequest(request)
              .pipe(take(1), takeUntil(this.destroyed$))
              .subscribe({
                next: templateId => {
                  this.form.markAsPristine();
                  this.store.dispatch(createTemplateSuccess({ templateId }));
                  this.isTemplateLimitError = false;
                  this.back();
                },
                error: (error: { status: number }) => {
                  if (error?.status === 409) {
                    this.isTemplateLimitError = true;
                    this.changeDetector.markForCheck();
                  } else {
                    // For other errors, dispatch failure action
                    this.showTemplateCreateError = true;
                    this.store.dispatch(createTemplateFailure());
                  }
                }
              });
          },
          error: error => {
            console.error('Error getting template request:', error);
            this.showTemplateCreateError = true;
            this.store.dispatch(createTemplateFailure());
          }
        });
    } else {
      this.templateNameCtrl?.markAsTouched();
      this.showFormValidationError = true;
      this.changeDetector.markForCheck();
    }
  }
  saveDuplicate(): void {
    if (this.form.valid) {
      this.getReportingTemplateRequest()
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe({
          next: request => {
            this.showBannerForTemplateLimit = true;
            this.store.dispatch(
              submitReportFormDuplicate({
                formData: request,
                actionType: ReportTemplateFormType.create
              })
            );
            this.form.markAsPristine();
            this.isTemplateLimitError = false;
            this.back();
          },
          error: error => {
            console.error('Error getting template request:', error);
            this.showTemplateCreateError = true;
            this.store.dispatch(createTemplateFailure());
          }
        });
    } else {
      this.templateNameCtrl?.markAsTouched();
      this.showFormValidationError = true;
      this.changeDetector.markForCheck();
    }
  }
  get templateNameCtrl(): AbstractControl | null {
    return this.form.get('templateName');
  }
  get descriptionCtrl(): AbstractControl | null {
    return this.form.get('description');
  }
  saveEditTemplate(): void {
    if (this.form.valid) {
      this.getReportingTemplateRequest()
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe({
          next: formData => {
            this.store.dispatch(
              saveCreateReportForm({
                formData
              })
            );
          },
          error: error => {
            console.error('Error getting template request:', error);
            this.showTemplateCreateError = true;
            this.store.dispatch(createTemplateFailure());
          }
        });
    } else {
      this.templateNameCtrl?.markAsTouched();
      this.showFormValidationError = true;
      this.changeDetector.markForCheck();
    }
  }
  formatDate(date: Date | undefined): Observable<string> {
    return this.dateTimeFormattingService.formatDate(date!.toString());
  }
  ngOnDestroy(): void {
    // if (this.createReportSectionSettings) {
    //   this.store.dispatch(clearCreateReportSectionSettings());
    // }
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
interface SerializedReportSection {
  order: number;
  isSelected: boolean;
  configClass: string;
  config: SerializedReportSectionConfig;
  width: number;
  appliedReportType: ReportType;
}

interface SerializedReportSectionConfig {
  name: string;
  appliedReportType: ReportType;
  nestedItems?: NestedItem[];
  availableDataSections?: DataSection[];
}

interface NestedItem {
  name: string;
  nestedItemSectionConfiguration: {
    config: string;
  };
}

interface DataSection {
  name: string;
  subSections?: SubSection[];
}

interface SubSection {
  name: string;
}
